/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Apr 16 2024
 **/

import { useToast } from "@/frameworks/designStuff/components";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { _T } from "@rfice/language";

import { useChat } from "@/frameworks/core/API";
import { APIErrorCode } from "@/frameworks/core/commons";

export default function useDMRoomActions() {
	const { dm } = useChat();
	const toast = useToast();

	const create = async ({ userIDs }: { userIDs: string[] }) => {
		try {
			const result = await dm.create.execute({
				userIds: userIDs
			});
			return result.data as WebServices.DirectMessage.Create.Data;
		} catch (err) {
			const error = err as RestAPIError;
			switch (error.code) {
				case APIErrorCode.NOT_ALLOWED_DM_INVITE: {
					toast.show({ type: "error", message: _T("해당 사용자는 DM 수신거부 상태입니다.") });
					return;
				}
			}
			throw error;
		}
	};
	/**
	 * 채팅방에 유저 추가
	 */
	const addUsers = async (args: { users: WebServices.RelatedUsers.List.Item[]; channelID: string }) => {
		return (
			await dm.addUsers.execute({
				roomId: args.channelID,
				userIds: args.users.map((user) => user.userId)
			})
		).data;
	};

	/**
	 * DM 숨기기 설정
	 */
	const hide = async ({ channelID }: { channelID: string }) => {
		return (await dm.hide.execute({ roomId: channelID })).data;
	};

	const checkExist = async ({ userIds }: { userIds: string[] }) => {
		return (await dm.checkExist.execute({ userIds })).data;
	};

	return {
		create,
		addUsers,
		hide,
		checkExist
	};
}
